import {
  Button,
  Stack,
  useTheme,
  Typography,
  Box,
} from '@nextiva/next-ui-components';
import React, { useState } from 'react';
import * as LaunchpadConstants from 'constants/launchpad';
import * as LaunchpadTypes from 'types/Launchpad';
import { LaunchpadGridCard } from '../Grid/Card/Card';

const testIdCreator = (tileDef: LaunchpadTypes.GridTileDef) =>
  `${LaunchpadConstants.TEST_ID}-${tileDef.id}-Tile`;

interface LaunchpadTileProps {
  tileDef: LaunchpadTypes.GridTileDef;
}

export const LaunchpadTile = ({
  tileDef = LaunchpadConstants.DEFAULT_GRID_TILE_DEF,
}: LaunchpadTileProps) => {
  const TEST_ID = testIdCreator(tileDef);

  const theme = useTheme();

  const [isHovered, setIsHovered] = useState(false);

  const withAction = tileDef.actionLabel && tileDef.actionHandler;

  const textColor = tileDef.isEnabled
    ? theme.palette.secondary.contrastText
    : theme.palette.grey['500'];

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid={TEST_ID}
      sx={{
        position: 'relative',
      }}
    >
      <LaunchpadGridCard>
        <Box
          sx={{
            paddingX: '32px',
            paddingY: '24px',
            minHeight: '156px',
          }}
        >
          <Stack direction="row" minHeight="60px">
            <Stack direction="column" flexGrow={1}>
              <Typography
                variant="button"
                data-testid={`${TEST_ID}-Title`}
                sx={{ color: tileDef.titleColor, mb: '8px', fontSize: '16px' }}
              >
                {tileDef.title}
              </Typography>

              <Box
                data-testid={`${TEST_ID}-Content`}
                sx={{ marginBottom: '16px' }}
              >
                {tileDef.content}
              </Box>

              {withAction && tileDef.isEnabled ? (
                <Stack direction="row">
                  <Button
                    color="primary"
                    variant="text"
                    sx={{
                      display: 'inline-flex',
                      margin: '-8px',
                    }}
                    onClick={() => tileDef.actionHandler()}
                    data-testid={`${TEST_ID}-Action`}
                  >
                    {tileDef.actionLabel}
                  </Button>
                </Stack>
              ) : null}
            </Stack>

            <Box
              sx={{
                color: `${textColor} !important`,
              }}
            >
              {tileDef.iconElement}
            </Box>
          </Stack>
        </Box>
      </LaunchpadGridCard>

      {!tileDef.isEnabled && tileDef.disabledText && isHovered ? (
        <Box
          sx={{
            paddingLeft: '8px',
            position: 'absolute',
            top: '100%',
            left: '0',
          }}
        >
          <Typography
            variant="caption"
            data-testid={`${TEST_ID}-Disabled-Text`}
            sx={{ color: theme.palette.grey['500'] }}
          >
            {tileDef.disabledText}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
